// External dependencies
import { ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import React from 'react';
import styled from 'styled-components';

// Internal dependencies
import '@/config/client/i18n';
import Report from './Report';
import type { ExperienceRecommendation } from '../../../shared/experience';

type PrimaryInteraction = 'guest' | 'host' | 'met';

interface RecommendProps {
  primaryInteraction: PrimaryInteraction;
  recommend: ExperienceRecommendation | null;
  report: boolean;
  reportMessage: string;
  onChangeRecommend: (recommend: ExperienceRecommendation) => void;
  onChangeReport: () => void;
  onChangeReportMessage: (message: string) => void;
}

const SadNotice = styled.div`
  margin: 30px 0;
`;

const RecommendationDivider = styled.div`
  height: 20px;
  width: 1px;
  background: #ccc;
  display: inline-block;
  float: left;
  margin: 12px 5px 0 5px;
`;

export default function Recommend({
  primaryInteraction,
  recommend,
  report,
  reportMessage,
  onChangeRecommend,
  onChangeReport,
  onChangeReportMessage,
}: RecommendProps) {
  const { t } = useTranslation('experiences') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };

  const recommendQuestions: Record<PrimaryInteraction, string> = {
    guest: t(
      'Besides your personal experience, would you recommend others to stay with them?',
    ) as string,
    host: t(
      'Besides your personal experience, would you recommend others to host them?',
    ) as string,
    met: t(
      'Besides your personal experience, would you recommend others to meet them?',
    ) as string,
  };

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h4 id="would-you-recommend-them-question">
          {recommendQuestions[primaryInteraction]}
        </h4>
      </div>
      <div className="panel-body">
        <ToggleButtonGroup
          type="radio"
          name="recommend"
          onChange={onChangeRecommend}
          value={recommend}
          aria-labelledby="would-you-recommend-them-question"
        >
          <ToggleButton
            id="recommend-yes"
            value="yes"
            variant="outline-secondary"
            size="lg"
          >
            {t('Yes') as string}
          </ToggleButton>
          <ToggleButton
            id="recommend-no"
            value="no"
            variant="outline-secondary"
            size="lg"
          >
            {t('No') as string}
          </ToggleButton>
          <RecommendationDivider />
          <ToggleButton
            id="recommend-unknown"
            value="unknown"
            variant="outline-secondary"
            size="lg"
          >
            {t('Skip') as string}
          </ToggleButton>
        </ToggleButtonGroup>
        {recommend === 'no' && (
          <SadNotice className="lead">
            {
              t(
                "We're sad to hear you didn't have a great experience using Trustroots!",
              ) as string
            }
            {' 😞'}
          </SadNotice>
        )}
        <Report
          onChangeReport={onChangeReport}
          onChangeReportMessage={onChangeReportMessage}
          report={report}
          reportMessage={reportMessage}
        />
      </div>
    </div>
  );
}
